/**
 * UpdateDataAgentModal Component
 *
 * Modal for updating an existing data agent.
 */

import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Textarea } from '../../../components/ui/Textarea';
import { Checkbox } from '../../../components/ui/Checkbox';
import { Alert } from '../../../components/ui/Alert';
import { FormField } from '../../../components/forms/FormField';
import { useUpdateDataAgent } from '../hooks/useUpdateDataAgent';
import type { DataAgent, DataAgentUpdateDTO } from '../../../types/data-agent.types';
import { DATA_AGENT_CONFIG_DEFAULTS } from '../../../types/data-agent.types';

// Form data type that includes flattened config fields
interface UpdateFormData {
  dataAgentName?: string;
  description?: string;
  isActive?: boolean;
  // Config fields (in seconds/minutes for user-friendly input)
  heartbeatIntervalSec?: number;
  pollIntervalSec?: number;
  pollIntervalBusySec?: number;
  queryTimeoutMin?: number;
  maxRowsPerQuery?: number;
}

// Helper to convert ms to seconds
const msToSec = (ms: number) => Math.round(ms / 1000);
// Helper to convert ms to minutes
const msToMin = (ms: number) => Math.round(ms / 60000);
// Helper to convert seconds to ms
const secToMs = (sec: number) => sec * 1000;
// Helper to convert minutes to ms
const minToMs = (min: number) => min * 60000;

interface UpdateDataAgentModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  dataAgent: DataAgent;
}

export const UpdateDataAgentModal: React.FC<UpdateDataAgentModalProps> = ({
  isOpen,
  onClose,
  tenantId,
  dataAgent,
}) => {
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);

  // Get current config values or defaults
  const currentConfig = dataAgent.config || {};
  const heartbeatMs = currentConfig.heartbeatIntervalMs ?? DATA_AGENT_CONFIG_DEFAULTS.heartbeatIntervalMs;
  const pollMs = currentConfig.pollIntervalMs ?? DATA_AGENT_CONFIG_DEFAULTS.pollIntervalMs;
  const pollBusyMs = currentConfig.pollIntervalBusyMs ?? DATA_AGENT_CONFIG_DEFAULTS.pollIntervalBusyMs;
  const queryTimeoutMs = currentConfig.queryTimeoutMs ?? DATA_AGENT_CONFIG_DEFAULTS.queryTimeoutMs;
  const maxRows = currentConfig.maxRowsPerQuery ?? DATA_AGENT_CONFIG_DEFAULTS.maxRowsPerQuery;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<UpdateFormData>({
    defaultValues: {
      dataAgentName: dataAgent.dataAgentName,
      description: dataAgent.description || '',
      isActive: dataAgent.isActive,
      heartbeatIntervalSec: msToSec(heartbeatMs),
      pollIntervalSec: msToSec(pollMs),
      pollIntervalBusySec: msToSec(pollBusyMs),
      queryTimeoutMin: msToMin(queryTimeoutMs),
      maxRowsPerQuery: maxRows,
    },
  });

  useEffect(() => {
    const cfg = dataAgent.config || {};
    reset({
      dataAgentName: dataAgent.dataAgentName,
      description: dataAgent.description || '',
      isActive: dataAgent.isActive,
      heartbeatIntervalSec: msToSec(cfg.heartbeatIntervalMs ?? DATA_AGENT_CONFIG_DEFAULTS.heartbeatIntervalMs),
      pollIntervalSec: msToSec(cfg.pollIntervalMs ?? DATA_AGENT_CONFIG_DEFAULTS.pollIntervalMs),
      pollIntervalBusySec: msToSec(cfg.pollIntervalBusyMs ?? DATA_AGENT_CONFIG_DEFAULTS.pollIntervalBusyMs),
      queryTimeoutMin: msToMin(cfg.queryTimeoutMs ?? DATA_AGENT_CONFIG_DEFAULTS.queryTimeoutMs),
      maxRowsPerQuery: cfg.maxRowsPerQuery ?? DATA_AGENT_CONFIG_DEFAULTS.maxRowsPerQuery,
    });
  }, [dataAgent, reset]);

  const { mutate: updateDataAgent, isPending } = useUpdateDataAgent(
    tenantId,
    dataAgent.dataAgentId,
    {
      onSuccess: () => {
        setSuccessMessage('Data agent updated successfully!');
        setErrorMessage('');
        setTimeout(() => {
          onClose();
          setSuccessMessage('');
        }, 1500);
      },
      onError: (error) => {
        setErrorMessage(error);
        setSuccessMessage('');
      },
    }
  );

  const onSubmit = (data: UpdateFormData) => {
    setErrorMessage('');
    setSuccessMessage('');

    // Transform form data to DTO format
    const dto: DataAgentUpdateDTO = {
      dataAgentName: data.dataAgentName,
      description: data.description,
      isActive: data.isActive,
      config: {
        heartbeatIntervalMs: data.heartbeatIntervalSec ? secToMs(data.heartbeatIntervalSec) : undefined,
        pollIntervalMs: data.pollIntervalSec ? secToMs(data.pollIntervalSec) : undefined,
        pollIntervalBusyMs: data.pollIntervalBusySec ? secToMs(data.pollIntervalBusySec) : undefined,
        queryTimeoutMs: data.queryTimeoutMin ? minToMs(data.queryTimeoutMin) : undefined,
        maxRowsPerQuery: data.maxRowsPerQuery,
      },
    };

    updateDataAgent(dto);
  };

  const handleClose = () => {
    if (!isPending) {
      setErrorMessage('');
      setSuccessMessage('');
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <Modal.Header>Update Data Agent</Modal.Header>
      <Modal.Body>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {errorMessage && <Alert variant="error">{errorMessage}</Alert>}
          {successMessage && <Alert variant="success">{successMessage}</Alert>}

          {/* Code (read-only) */}
          <FormField label="Agent Code" hint="Code cannot be changed after creation">
            <Input value={dataAgent.dataAgentCode} disabled className="bg-gray-50 font-mono" />
          </FormField>

          <FormField
            label="Agent Name"
            required
            error={errors.dataAgentName?.message}
          >
            <Input
              {...register('dataAgentName', {
                required: 'Agent name is required',
                minLength: { value: 2, message: 'Name must be at least 2 characters' },
                maxLength: { value: 100, message: 'Name must be less than 100 characters' },
              })}
              placeholder="Production Agent"
              error={!!errors.dataAgentName}
            />
          </FormField>

          <FormField
            label="Description"
            error={errors.description?.message}
          >
            <Textarea
              {...register('description', {
                maxLength: { value: 500, message: 'Description must be less than 500 characters' },
              })}
              placeholder="Data agent for production environment..."
              rows={3}
            />
          </FormField>

          <FormField label="Status">
            <div className="flex items-center">
              <Checkbox {...register('isActive')} />
              <span className="ml-2 text-sm text-gray-700">Active</span>
            </div>
          </FormField>

          {/* Advanced Configuration (Collapsible) */}
          <div className="border-t pt-4">
            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900"
            >
              {showAdvanced ? (
                <ChevronDownIcon className="h-4 w-4" />
              ) : (
                <ChevronRightIcon className="h-4 w-4" />
              )}
              Advanced Configuration
            </button>

            {showAdvanced && (
              <div className="mt-4 space-y-4 rounded-lg bg-gray-50 p-4">
                <p className="text-xs text-gray-500 mb-3">
                  Configure agent operational parameters. Changes take effect on the next heartbeat cycle.
                </p>

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    label="Heartbeat Interval"
                    hint="30-300 seconds"
                    error={errors.heartbeatIntervalSec?.message}
                  >
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        {...register('heartbeatIntervalSec', {
                          valueAsNumber: true,
                          min: { value: 30, message: 'Min 30 seconds' },
                          max: { value: 300, message: 'Max 300 seconds' },
                        })}
                        className="w-24"
                        error={!!errors.heartbeatIntervalSec}
                      />
                      <span className="text-sm text-gray-500">sec</span>
                    </div>
                  </FormField>

                  <FormField
                    label="Poll Interval (Idle)"
                    hint="60-600 seconds"
                    error={errors.pollIntervalSec?.message}
                  >
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        {...register('pollIntervalSec', {
                          valueAsNumber: true,
                          min: { value: 60, message: 'Min 60 seconds' },
                          max: { value: 600, message: 'Max 600 seconds' },
                        })}
                        className="w-24"
                        error={!!errors.pollIntervalSec}
                      />
                      <span className="text-sm text-gray-500">sec</span>
                    </div>
                  </FormField>

                  <FormField
                    label="Poll Interval (Busy)"
                    hint="5-60 seconds"
                    error={errors.pollIntervalBusySec?.message}
                  >
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        {...register('pollIntervalBusySec', {
                          valueAsNumber: true,
                          min: { value: 5, message: 'Min 5 seconds' },
                          max: { value: 60, message: 'Max 60 seconds' },
                        })}
                        className="w-24"
                        error={!!errors.pollIntervalBusySec}
                      />
                      <span className="text-sm text-gray-500">sec</span>
                    </div>
                  </FormField>

                  <FormField
                    label="Query Timeout"
                    hint="1-1440 minutes (24h)"
                    error={errors.queryTimeoutMin?.message}
                  >
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        {...register('queryTimeoutMin', {
                          valueAsNumber: true,
                          min: { value: 1, message: 'Min 1 minute' },
                          max: { value: 1440, message: 'Max 1440 minutes (24h)' },
                        })}
                        className="w-24"
                        error={!!errors.queryTimeoutMin}
                      />
                      <span className="text-sm text-gray-500">min</span>
                    </div>
                  </FormField>
                </div>

                <FormField
                  label="Max Rows Per Query"
                  hint="10,000 - 1,000,000 rows"
                  error={errors.maxRowsPerQuery?.message}
                >
                  <Input
                    type="number"
                    {...register('maxRowsPerQuery', {
                      valueAsNumber: true,
                      min: { value: 10000, message: 'Min 10,000 rows' },
                      max: { value: 1000000, message: 'Max 1,000,000 rows' },
                    })}
                    className="w-40"
                    error={!!errors.maxRowsPerQuery}
                  />
                </FormField>
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={handleClose} disabled={isPending}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={isPending}>
              Update Agent
            </Button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};
